import { useState, useCallback } from 'react';
import { apiService } from '../../services/api';
import type { ParseResult, NewCategoryInfo } from './types';
import type { BulkUploadRequest } from '../../types';
import { useExcelParser } from './hooks/useExcelParser';
import { useBulkUpload } from './hooks/useBulkUpload';
import { useExcelExporter } from './hooks/useExcelExporter';
import { UploadStep } from './steps/UploadStep';
import { PreviewStep } from './steps/PreviewStep';
import { ResultStep } from './steps/ResultStep';

type Step = 'upload' | 'preview' | 'result';

const stepLabels: { key: Step; label: string }[] = [
  { key: 'upload', label: 'アップロード' },
  { key: 'preview', label: 'プレビュー' },
  { key: 'result', label: '結果' },
];

export const BulkUploadPage: React.FC = () => {
  const [step, setStep] = useState<Step>('upload');
  const [fileName, setFileName] = useState('');
  const [newCategories, setNewCategories] = useState<NewCategoryInfo[]>([]);
  const {
    parseResult,
    parseError,
    isParsing,
    parseFile,
    reset: resetParser,
    setParseResult,
  } = useExcelParser();
  const { isUploading, uploadError, result, upload, reset: resetUpload } = useBulkUpload();
  const {
    isExporting,
    exportError,
    exportWarnings,
    exportData,
    isDownloadingTemplate,
    downloadTemplateError,
    downloadTemplate,
  } = useExcelExporter();

  const handleFileSelected = useCallback(
    async (file: File) => {
      setFileName(file.name);
      try {
        const [categories, allFaqs, symptomsResp] = await Promise.all([
          apiService.getCategories(),
          apiService.getAllFaqs(),
          apiService.getAllSymptoms(),
        ]);
        // AllSymptomsItem[] → SymptomListItem[] に変換（parseFile が期待する型に合わせる）
        const existingSymptoms = symptomsResp.items.map((s) => ({
          categoryId: s.categoryId,
          symptomName: s.symptomName,
          updatedAt: '',
        }));
        await parseFile(file, categories, allFaqs, existingSymptoms);
        setStep('preview');
      } catch (err) {
        console.error('Failed to fetch existing data:', err);
      }
    },
    [parseFile],
  );

  const handleSubmit = useCallback(async () => {
    if (!parseResult) return;
    const selectedFaqs = parseResult.faqs.filter((r) => r.selected && r.status !== 'error');
    const selectedSymptoms = parseResult.symptoms.filter((r) => r.selected && r.status !== 'error');

    const request: BulkUploadRequest = {
      faqs: selectedFaqs.map((r) => ({
        question: r.data.question,
        answer: r.data.answer,
        categoryName: r.data.categoryName,
        faqCategoryName: r.data.faqCategoryName,
        isInitial: r.data.isInitial,
        action: r.duplicateAction,
      })),
      symptoms: selectedSymptoms.map((r) => ({
        symptomName: r.data.symptomName,
        categoryName: r.data.categoryName,
        cause: r.data.cause,
        solution: r.data.solution,
        action: r.duplicateAction,
      })),
      newCategories: parseResult.newCategories,
    };

    setNewCategories(parseResult.newCategories);
    const success = await upload(request);
    if (success) {
      setStep('result');
    }
  }, [parseResult, upload]);

  const handleReset = useCallback(() => {
    setStep('upload');
    setFileName('');
    resetParser();
    resetUpload();
  }, [resetParser, resetUpload]);

  return (
    <div className="mx-auto max-w-5xl p-6">
      <h2 className="mb-6 text-xl font-bold text-gray-800">一括登録</h2>

      {/* Stepper */}
      <div className="mb-8 flex items-center gap-6">
        {stepLabels.map((s, i) => {
          const stepIndex = stepLabels.findIndex((sl) => sl.key === step);
          const isCompleted = i < stepIndex;
          const isCurrent = s.key === step;
          return (
            <div key={s.key} className="flex items-center gap-2">
              {i > 0 && (
                <div
                  className={`mr-4 h-0.5 w-10 ${isCompleted ? 'bg-green-500' : 'bg-gray-300'}`}
                />
              )}
              <span
                className={`flex h-7 w-7 items-center justify-center rounded-full text-sm font-bold text-white ${isCompleted ? 'bg-green-500' : isCurrent ? 'bg-blue-600' : 'bg-gray-300'}`}
              >
                {i + 1}
              </span>
              <span
                className={`text-sm ${isCompleted ? 'text-green-600' : isCurrent ? 'font-bold text-blue-600' : 'text-gray-400'}`}
              >
                {s.label}
              </span>
            </div>
          );
        })}
      </div>

      {step === 'upload' && (
        <UploadStep
          onFileSelected={handleFileSelected}
          isParsing={isParsing}
          parseError={parseError}
          onExport={exportData}
          isExporting={isExporting}
          exportError={exportError}
          exportWarnings={exportWarnings}
          onDownloadTemplate={downloadTemplate}
          isDownloadingTemplate={isDownloadingTemplate}
          downloadTemplateError={downloadTemplateError}
        />
      )}
      {step === 'preview' && parseResult && (
        <PreviewStep
          fileName={fileName}
          parseResult={parseResult}
          onUpdateParseResult={(r: ParseResult) => setParseResult(r)}
          onSubmit={handleSubmit}
          onBack={handleReset}
          isUploading={isUploading}
          uploadError={uploadError}
        />
      )}
      {step === 'result' && result && (
        <ResultStep
          fileName={fileName}
          result={result}
          newCategories={newCategories}
          onUploadAnother={handleReset}
        />
      )}
    </div>
  );
};
